import { selectScaleOptionSchema } from "@/components/forms/form-editor.types.ts";
import { FormEditScaleOrSelectOptionModal } from "@/components/forms/widgets/FormEditScaleOrSelectOptionModal.tsx";
import { OptionsBulkAddModal } from "@/components/options/OptionsBulkAddModal.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Avatar,
  Box,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Input,
  Select,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useFormScalesSave } from "@/queries/use-scales.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFormScaleType, ApiFormViewScalePayload } from "@/types/forms/form-scale.types.ts";
import { ApiFormViewScale } from "@/types/forms/form-view.types.ts";
import { FormScaleTypeItems } from "@/types/forms/form.types.ts";
import { ApiFormElementSelectOptionEdit } from "@/types/forms/form-edit.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface ScaleEditModalProps extends ModalProps {
  formViewScale?: ApiFormViewScale;
}

enum ScaleField {
  scale = "scale",
  scale2 = "scale2",
}

type ScaleForm = {
  title: string;
  type: ApiFormScaleType;
  scale: ApiFormElementSelectOptionEdit[];
  scale2?: ApiFormElementSelectOptionEdit[];
};

export default function ScaleEditModal({ formViewScale, ...props }: ScaleEditModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { id } = props;

  const scaleSchema = z
    .object({
      title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
      type: z.enum(ApiFormScaleType),
      scale: z.array(selectScaleOptionSchema).min(1),
      scale2: z.array(selectScaleOptionSchema).optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data.type === ApiFormScaleType.double_scale &&
        (data.scale2 == null || data.scale2.length < 1)
      ) {
        ctx.addIssue({ code: "custom", message: t("errors:field_required"), path: ["scale2"] });
      }
    });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ScaleForm>({
    mode: "onSubmit",
    resolver: zodResolver(scaleSchema) as Resolver<ScaleForm>,
    defaultValues: {
      title: formViewScale?.title ?? "",
      type: formViewScale?.type ?? ApiFormScaleType.single_scale,
      scale: formViewScale?.scale.sort((a, b) => a.sort - b.sort) ?? [],
      scale2: formViewScale?.scale2?.sort((a, b) => a.sort - b.sort) ?? [],
    },
  });

  const scaleSave = useFormScalesSave(control);

  const onSubmit = handleSubmit(async (data) => {
    const payload: ApiFormViewScalePayload = {
      title: data.title,
      type: data.type,
      scale: data.scale,
      scale2: data.type === ApiFormScaleType.double_scale ? data.scale2 : undefined,
    };

    try {
      const responseFormViewScale = await scaleSave.mutateAsync({
        id: formViewScale?.id,
        payload,
      });

      if (formViewScale) {
        analyticsService.trackEvent(analyticEvents.formScales.edited, {
          [analyticProperties.id]: formViewScale.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.formScales.created, {
          [analyticProperties.id]: responseFormViewScale.id,
        });
      }

      close(id);
    } catch {}
  });

  const scale = watch("scale") ?? [];
  const scale2 = watch("scale2") ?? [];
  const type = watch("type");

  const handleDragEnd = (result: DropResult, field: ScaleField) => {
    if (!result.destination) return;

    const items = [...(field === ScaleField.scale ? scale : scale2)];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      sort: index,
    }));

    setValue(field, updatedItems);
  };

  const addOption = (field: ScaleField) => {
    openModal(FormEditScaleOrSelectOptionModal, {
      isSelect: false,
      item: {
        emoji: null,
        title: null,
        description: null,
        value: null,
      },
      onSave: async (item) => {
        const currentItems = field === ScaleField.scale ? scale : scale2;
        setValue(field, [...currentItems, item]);
      },
    });
  };

  const addMultipleOptions = (field: ScaleField) => {
    openModal(OptionsBulkAddModal, {
      onAdd: (titles: string[]) => {
        const currentItems = getValues(field) ?? [];
        const newOptions = titles.map((title) => ({
          title,
          description: null,
          emoji: null,
          value: null,
        }));
        setValue(field, [...currentItems, ...newOptions], {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
    });
  };

  const editOption = (index: number, field: ScaleField) => {
    const currentItems = field === ScaleField.scale ? scale : scale2;
    const option = currentItems[index];
    openModal(FormEditScaleOrSelectOptionModal, {
      isSelect: false,
      item: option,
      onSave: async (item) => {
        const updatedOptions = [...currentItems];
        updatedOptions[index] = item;
        setValue(field, updatedOptions);
      },
    });
  };

  const removeOption = (index: number, field: ScaleField) => {
    const currentItems = field === ScaleField.scale ? scale : scale2;
    const updatedOptions = [...currentItems];
    updatedOptions.splice(index, 1);

    const reindexedOptions = updatedOptions.map((item, idx) => ({
      ...item,
      sort: idx,
    }));

    setValue(field, reindexedOptions);
  };

  const renderScaleOptions = (field: ScaleField, items: ApiFormElementSelectOptionEdit[]) => (
    <>
      <Stack gap={"sm"} className={"mt-4"}>
        <Title
          header={
            type === ApiFormScaleType.single_scale
              ? t("common:options")
              : field === ScaleField.scale
                ? t("core:first_scale_options")
                : t("core:second_scale_options")
          }
          size={"xs"}
          paddingTop={false}
          paddingBottom={true}
        />
        <DragDropContext onDragEnd={(result) => handleDragEnd(result, field)}>
          <Droppable droppableId={`${field}-options`}>
            {(provided) => (
              <div className="flex flex-col" {...provided.droppableProps} ref={provided.innerRef}>
                {items.map((option, index) => (
                  <Draggable
                    key={`${field}-option-${index}`}
                    draggableId={`${field}-option-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        className="group/option my-1 rounded-xs border border-dark/5 bg-dark/5 px-4 py-2"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <Box>
                          <Stack direction="horizontal" justify="between" items="center">
                            <Stack direction="horizontal" items="center" gap="xs">
                              {option.emoji && <Avatar emoji={option.emoji} variant="plain" />}
                              {option.title && <Text className="text-title">{option.title}</Text>}
                            </Stack>
                            <Stack
                              direction="horizontal"
                              gap="sm"
                              items="center"
                              className={
                                snapshot.isDragging
                                  ? "opacity-100"
                                  : "opacity-0 pointer-events-none transition-opacity group-hover/option:opacity-100 group-hover/option:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto"
                              }
                            >
                              <ButtonIcon
                                variant="plain"
                                icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                                onClick={() => removeOption(index, field)}
                              />
                              <ButtonIcon
                                variant="plain"
                                icon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                                onClick={() => editOption(index, field)}
                              />
                              <Box {...provided.dragHandleProps}>
                                <FontAwesomeIcon
                                  className="m-2"
                                  icon="fa-light fa-grip-dots-vertical"
                                />
                              </Box>
                            </Stack>
                          </Stack>
                        </Box>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <Stack direction="horizontal" gap="sm" justify="between">
          <Button
            variant="plain"
            className={"text-center w-1/2"}
            onClick={() => addOption(field)}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:add_option")}
          </Button>
          <Button
            variant="plain"
            className={"text-center w-1/2"}
            onClick={() => addMultipleOptions(field)}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-rectangle-history-circle-plus" />}
          >
            {t("common:add_multiple")}
          </Button>
        </Stack>
      </Stack>
      {(field === ScaleField.scale ? errors.scale : errors.scale2) && (
        <div className="text-danger text-sm mb-2">{t("common:options_at_least_one")}</div>
      )}
    </>
  );

  return (
    <Modal
      {...props}
      variant="side"
      title={t("common:edit")}
      layout="stretch"
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap="sm">
          <ServerErrorField errors={errors} />
          <Button
            size="lg"
            className="w-full"
            type="submit"
            onClick={onSubmit}
            isLoading={scaleSave.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap="lg">
          <Input
            label={t("common:title")}
            type="text"
            {...register("title")}
            required
            error={errors.title?.message}
          />
          <Controller
            control={control}
            name="type"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("common:type")}
                emptyTitle={t("common:select")}
                options={FormScaleTypeItems()}
                value={value}
                onChange={onChange}
                error={errors.type?.message}
              />
            )}
          />
          {renderScaleOptions(ScaleField.scale, scale)}
          {type === ApiFormScaleType.double_scale && renderScaleOptions(ScaleField.scale2, scale2)}
        </Stack>
      </form>
    </Modal>
  );
}
